import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Container, Card, Table, Button, Row, Col, Form, Alert } from 'react-bootstrap';
import {
  FaPlus, FaEdit, FaTrash, FaSearch, FaFileUpload, FaDownload,
  FaExclamationTriangle, FaCheck, FaFilter, FaTimes,
  FaBuilding, FaUsers, FaClock, FaLock, FaMapMarkerAlt, FaAddressCard, FaCalendarAlt,
  FaInfoCircle, FaLink, FaUnlink
} from 'react-icons/fa';
import supabase from '../../supabaseClient';
import { useAuth } from '../../hooks/useAuth';

// Função para formatar CNPJ com máscara
const formatCnpj = (cnpj) => {
  if (!cnpj) return '';
  const numericCnpj = cnpj.replace(/\D/g, '');
  
  if (numericCnpj.length <= 2) {
    return numericCnpj;
  } else if (numericCnpj.length <= 5) {
    return `${numericCnpj.slice(0, 2)}.${numericCnpj.slice(2)}`;
  } else if (numericCnpj.length <= 8) {
    return `${numericCnpj.slice(0, 2)}.${numericCnpj.slice(2, 5)}.${numericCnpj.slice(5)}`;
  } else if (numericCnpj.length <= 12) {
    return `${numericCnpj.slice(0, 2)}.${numericCnpj.slice(2, 5)}.${numericCnpj.slice(5, 8)}/${numericCnpj.slice(8)}`;
  } else {
    return `${numericCnpj.slice(0, 2)}.${numericCnpj.slice(2, 5)}.${numericCnpj.slice(5, 8)}/${numericCnpj.slice(8, 12)}-${numericCnpj.slice(12, 14)}`;
  }
};

// Função para remover máscara do CNPJ
const removeCnpjMask = (cnpj) => {
  return cnpj ? cnpj.replace(/\D/g, '') : '';
};

// Função para formatar CPF com máscara
const formatCpf = (cpf) => {
  if (!cpf) return '';
  const numericCpf = cpf.replace(/\D/g, '');
  
  if (numericCpf.length <= 3) {
    return numericCpf;
  } else if (numericCpf.length <= 6) {
    return `${numericCpf.slice(0, 3)}.${numericCpf.slice(3)}`;
  } else if (numericCpf.length <= 9) {
    return `${numericCpf.slice(0, 3)}.${numericCpf.slice(3, 6)}.${numericCpf.slice(6)}`;
  } else {
    return `${numericCpf.slice(0, 3)}.${numericCpf.slice(3, 6)}.${numericCpf.slice(6, 9)}-${numericCpf.slice(9, 11)}`;
  }
};

// Função para remover máscara do CPF
const removeCpfMask = (cpf) => {
  return cpf ? cpf.replace(/\D/g, '') : '';
};

// Função para formatar telefone com máscara
const formatTelefone = (telefone) => {
  if (!telefone) return '';
  const numericTelefone = telefone.replace(/\D/g, '');
  
  if (numericTelefone.length <= 2) {
    return `(${numericTelefone}`;
  } else if (numericTelefone.length <= 7) {
    return `(${numericTelefone.slice(0, 2)}) ${numericTelefone.slice(2)}`;
  } else {
    return `(${numericTelefone.slice(0, 2)}) ${numericTelefone.slice(2, 7)}-${numericTelefone.slice(7, 11)}`;
  }
};

// Função para remover máscara do telefone
const removeTelefoneMask = (telefone) => {
  return telefone ? telefone.replace(/\D/g, '') : '';
};

const EmpresasMontadoras = () => {
  const { user } = useAuth();
  
  // Estado inicial do formulário
  const initialFormData = {
    nome: '',
    cnpj: '',
    responsavel: '',
    cpf_responsavel: '',
    email: '',
    telefone: '',
    selectedExpositores: [],
    ativo: true
  };
  
  // --- ESTADOS ---
  const [isExpositor, setIsExpositor] = useState(false);
  const [checkingProfile, setCheckingProfile] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showUnlink, setShowUnlink] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [unlinkingCompany, setUnlinkingCompany] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [activeModalTab, setActiveModalTab] = useState('empresa');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados para dados do banco
  const [empresasMontadoras, setEmpresasMontadoras] = useState([]);
  const [vinculosMontadoras, setVinculosMontadoras] = useState([]);
  const [expositores, setExpositores] = useState([]);
  const [eventos, setEventos] = useState([]);
  
  // Estados para controle do dropdown de expositores
  const [showExpositorDropdown, setShowExpositorDropdown] = useState(false);
  const [expositorSearch, setExpositorSearch] = useState('');
  const [loadingExpositores, setLoadingExpositores] = useState(false);
  
  // Estado para dados do expositor logado
  const [expositorLogado, setExpositorLogado] = useState(null);
  
  // Estados para busca de CNPJ existente
  const [checkingCNPJ, setCheckingCNPJ] = useState(false);
  const [cnpjAutoFilled, setCnpjAutoFilled] = useState(false);
  
  // Referência para o dropdown de expositores
  const expositorDropdownRef = useRef(null);

  // Estado para formulário de empresa montadora
  const [formData, setFormData] = useState(initialFormData);
  
  // Filtros
  const [filters, setFilters] = useState({
    search: ''
  });

  // Chave para armazenar o estado do formulário no localStorage
  const FORM_STATE_KEY = 'empresa_montadora_form_state';

  // --- FUNÇÕES DE UTILITY ---
  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setEditingCompany(null);
    setCnpjAutoFilled(false);
    setActiveModalTab('empresa');
    setShowForm(false);
  }, []);

  // Função para salvar estado no localStorage com verificação de segurança
  const saveFormState = useCallback(() => {
    if (!showForm) return;
    
    try {
      const stateToSave = {
        formData: formData || initialFormData,
        editingCompany,
        activeModalTab: activeModalTab || 'empresa',
        showForm,
        cnpjAutoFilled: cnpjAutoFilled || false
      };
      
      // Salvar expositores selecionados (apenas IDs para evitar objetos circulares)
      if (formData?.selectedExpositores && Array.isArray(formData.selectedExpositores) && formData.selectedExpositores.length > 0) {
        stateToSave.selectedExpositorIds = formData.selectedExpositores.map(exp => exp?.id).filter(Boolean);
      }
      
      localStorage.setItem(FORM_STATE_KEY, JSON.stringify(stateToSave));
      console.log('💾 Estado do formulário salvo');
    } catch (error) {
      console.error('Erro ao salvar estado do formulário:', error);
    }
  }, [formData, editingCompany, activeModalTab, showForm, cnpjAutoFilled]);

  // Função para restaurar estado do localStorage com verificação de segurança
  const restoreFormState = useCallback(() => {
    try {
      const savedState = localStorage.getItem(FORM_STATE_KEY);
      if (!savedState) return;
      
      const parsedState = JSON.parse(savedState);
      
      // Verificar se o estado é válido
      if (parsedState && typeof parsedState === 'object') {
        setFormData(parsedState.formData || initialFormData);
        setEditingCompany(parsedState.editingCompany || null);
        setActiveModalTab(parsedState.activeModalTab || 'empresa');
        setShowForm(Boolean(parsedState.showForm));
        setCnpjAutoFilled(Boolean(parsedState.cnpjAutoFilled));
        
        // Restaurar expositores selecionados após carregar lista de expositores
        if (parsedState.selectedExpositorIds && Array.isArray(parsedState.selectedExpositorIds) && expositores.length > 0) {
          const selectedExps = parsedState.selectedExpositorIds
            .map(id => expositores.find(exp => exp?.id === id))
            .filter(Boolean);
            
          if (selectedExps.length > 0) {
            setFormData(prev => ({
              ...prev,
              selectedExpositores: selectedExps
            }));
          }
        }
        
        console.log('🔄 Estado do formulário restaurado');
      }
    } catch (error) {
      console.error('Erro ao restaurar estado do formulário:', error);
      localStorage.removeItem(FORM_STATE_KEY);
    }
  }, [expositores]);

  // --- LÓGICA DE VERIFICAÇÃO DE PERFIL ---
  const checkUserProfile = useCallback(async () => {
    if (!user?.id) {
      setCheckingProfile(false);
      return;
    }
    
    try {
      setCheckingProfile(true);
      const { data, error: rpcError } = await supabase.rpc('is_user_expositor', { p_user_id: user.id });

      if (rpcError) throw rpcError;
      
      setIsExpositor(Boolean(data));
    } catch (err) {
      console.error('Erro ao verificar perfil do usuário:', err);
      setError('Não foi possível verificar as permissões do usuário.');
      setIsExpositor(false);
    } finally {
      setCheckingProfile(false);
    }
  }, [user?.id]);

  // --- FUNÇÕES DE FETCH ---
  const fetchExpositorLogado = useCallback(async () => {
    if (!user?.id) return null;

    try {
      const { data: expositorData, error: expositorError } = await supabase
        .from('caex_expositores')
        .select('id, nome_empresa, cnpj, responsavel, email, telefone, ativo, usuario_id')
        .eq('usuario_id', user.id)
        .eq('ativo', true)
        .single();

      if (expositorError && expositorError.code !== 'PGRST116') throw expositorError;

      setExpositorLogado(expositorData);
      return expositorData;
      
    } catch (error) {
      console.error('Erro ao buscar dados do expositor logado:', error);
      return null;
    }
  }, [user?.id]);

  const fetchVinculosMontadoras = useCallback(async (expositorAtual = null) => {
    try {
      let query = supabase
        .from('caex_montadora_expositor_evento')
        .select(`
          *,
          caex_empresas_montadoras (
            id,
            nome,
            cnpj
          ),
          caex_expositores (
            id,
            nome_empresa
          ),
          caex_eventos (
            id,
            nome
          )
        `);

      if (isExpositor && expositorAtual) {
        query = query.eq('expositor_id', expositorAtual.id);
      }

      const { data, error } = await query.order('id', { ascending: false });
      
      if (error) throw error;
      
      setVinculosMontadoras(data || []);
      return data || [];
    } catch (error) {
      console.error('Erro ao carregar vínculos de montadoras:', error);
      return [];
    }
  }, [isExpositor]);

  const fetchExpositores = useCallback(async () => {
    try {
      setLoadingExpositores(true);
      
      const { data, error } = await supabase
        .from('caex_expositores')
        .select('id, nome_empresa, cnpj')
        .eq('ativo', true)
        .order('nome_empresa');
      
      if (error) throw error;
      
      setExpositores(data || []);
    } catch (error) {
      console.error('Erro ao carregar expositores:', error);
    } finally {
      setLoadingExpositores(false);
    }
  }, []);

  const fetchEventos = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('caex_eventos')
        .select('id, nome')
        .eq('ativo', true)
        .order('nome');
      
      if (error) throw error;
      
      setEventos(data || []);
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
    }
  }, []);

  const fetchEmpresasMontadoras = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      let expositorAtual = null;
      if (isExpositor) {
        expositorAtual = await fetchExpositorLogado();
        if (!expositorAtual) {
          setEmpresasMontadoras([]);
          setLoading(false);
          return;
        }
      }
      
      if (isExpositor && expositorAtual) {
        // Para expositores, mostrar apenas empresas já vinculadas
        const { data: vinculosData, error: vinculosError } = await supabase
          .from('caex_montadora_expositor_evento')
          .select('montadora_id, ativo')
          .eq('expositor_id', expositorAtual.id)
          .eq('ativo', true);

        if (vinculosError) throw vinculosError;

        if (vinculosData && vinculosData.length > 0) {
          const montadoraIds = [...new Set(vinculosData.map(v => v.montadora_id))];
          const { data, error } = await supabase
            .from('caex_empresas_montadoras')
            .select('*')
            .in('id', montadoraIds)
            .order('id', { ascending: false });

          if (error) throw error;
          setEmpresasMontadoras((data || []).map(e => ({ ...e, cnpj: formatCnpj(e.cnpj || '') })));
        } else {
          setEmpresasMontadoras([]);
        }
      } else if (!isExpositor) {
        const { data, error } = await supabase
          .from('caex_empresas_montadoras')
          .select('*')
          .order('id', { ascending: false });
        
        if (error) throw error;
        setEmpresasMontadoras((data || []).map(e => ({ ...e, cnpj: formatCnpj(e.cnpj || '') })));
      }
      
      await fetchVinculosMontadoras(expositorAtual);
    } catch (error) {
      console.error('Erro ao carregar empresas montadoras:', error);
      setError('Não foi possível carregar a lista de empresas montadoras.');
    } finally {
      setLoading(false);
    }
  }, [isExpositor, fetchExpositorLogado, fetchVinculosMontadoras]);

  // Verificar CNPJ existente
  const checkExistingCNPJ = useCallback(async (cnpj) => {
    if (!cnpj || cnpj.length < 18 || editingCompany) return;
    
    const cnpjNumerico = removeCnpjMask(cnpj);
    if (cnpjNumerico.length !== 14) return;
    
    try {
      setCheckingCNPJ(true);
      
      const { data, error } = await supabase
        .from('caex_empresas_montadoras')
        .select('*')
        .eq('cnpj', cnpjNumerico)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        console.error('Erro ao buscar CNPJ:', error);
        return;
      }
      
      if (data) {
        let expositorVinculadosExistentes = [];
        if (!isExpositor) {
          const expositorVinculados = vinculosMontadoras
            .filter(vinculo => vinculo.montadora_id === data.id)
            .map(vinculo => ({
              id: vinculo.expositor_id,
              nome_empresa: vinculo.caex_expositores?.nome_empresa || 'N/A'
            }))
            .filter((expositor, index, self) => 
              index === self.findIndex(e => e.id === expositor.id)
            );
          
          expositorVinculadosExistentes = expositorVinculados;
        }
        
        setFormData(prev => ({
          ...prev,
          nome: data.nome || '',
          responsavel: data.responsavel || '',
          cpf_responsavel: data.cpf_responsavel ? formatCpf(data.cpf_responsavel) : '',
          email: data.email || '',
          telefone: data.telefone ? formatTelefone(data.telefone) : '',
          ativo: data.ativo !== undefined ? data.ativo : true,
          selectedExpositores: expositorVinculadosExistentes
        }));
        
        setCnpjAutoFilled(true);
        setEditingCompany(data);
      } else {
        setCnpjAutoFilled(false);
      }
    } catch (error) {
      console.error('Erro ao verificar CNPJ existente:', error);
    } finally {
      setCheckingCNPJ(false);
    }
  }, [editingCompany, isExpositor, vinculosMontadoras]);

  // --- USE EFFECTS COM DEPENDÊNCIAS SIMPLIFICADAS ---
  
  // Verificação de perfil do usuário
  useEffect(() => {
    if (user?.id) {
      checkUserProfile();
    }
  }, [user?.id]);

  // Carregar dados iniciais
  useEffect(() => {
    if (!checkingProfile && user?.id) {
      fetchEmpresasMontadoras();
      if (!isExpositor) {
        fetchExpositores();
        fetchEventos();
      }
    }
  }, [checkingProfile, isExpositor, user?.id]);

  // Verificar CNPJ existente com debounce (apenas se não estiver editando)
  useEffect(() => {
    if (!formData.cnpj || editingCompany) return;
    
    const timeoutId = setTimeout(() => {
      checkExistingCNPJ(formData.cnpj);
    }, 800);
    
    return () => clearTimeout(timeoutId);
  }, [formData.cnpj, editingCompany, checkExistingCNPJ]);

  // Salvar estado do formulário quando a página perde foco
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        saveFormState();
      } else if (document.visibilityState === 'visible') {
        restoreFormState();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [saveFormState, restoreFormState]);

  // Limpar estado salvo quando o formulário é fechado
  useEffect(() => {
    if (!showForm) {
      localStorage.removeItem(FORM_STATE_KEY);
    }
  }, [showForm]);

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (expositorDropdownRef.current && !expositorDropdownRef.current.contains(event.target)) {
        setShowExpositorDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Impedir o scroll quando o modal estiver aberto
  useEffect(() => {
    if (showForm || showDelete || showUnlink) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showForm, showDelete, showUnlink]);

  // --- HANDLERS E FUNÇÕES DE UI ---
  const handleExpositorSearch = useCallback((e) => {
    setExpositorSearch(e.target.value);
  }, []);

  const filteredExpositores = useMemo(() => {
    if (!Array.isArray(expositores)) return [];
    return expositores.filter(expositor =>
      expositor?.nome_empresa?.toLowerCase().includes(expositorSearch.toLowerCase()) ||
      expositor?.cnpj?.includes(expositorSearch)
    );
  }, [expositores, expositorSearch]);

  const handleAddExpositor = useCallback((expositor) => {
    if (!expositor?.id) return;
    
    setFormData(prev => {
      const currentSelected = prev.selectedExpositores || [];
      if (!currentSelected.some(e => e.id === expositor.id)) {
        return {
          ...prev,
          selectedExpositores: [...currentSelected, expositor]
        };
      }
      return prev;
    });
    setExpositorSearch('');
  }, []);

  const handleRemoveExpositor = useCallback((expositorId) => {
    setFormData(prev => ({
      ...prev,
      selectedExpositores: (prev.selectedExpositores || []).filter(e => e.id !== expositorId)
    }));
  }, []);

  const handleAddCompany = useCallback(() => {
    resetForm();
    setShowForm(true);
  }, [resetForm]);

  const handleEditCompany = useCallback((company) => {
    if (!company) return;
    
    setEditingCompany(company);
    
    let expositorVinculados = [];
    
    if (isExpositor && expositorLogado) {
      expositorVinculados = vinculosMontadoras
        .filter(vinculo => vinculo.montadora_id === company.id && vinculo.expositor_id === expositorLogado.id)
        .map(vinculo => ({
          id: vinculo.expositor_id,
          nome_empresa: vinculo.caex_expositores?.nome_empresa || expositorLogado.nome_empresa
        }))
        .filter((expositor, index, self) => 
          index === self.findIndex(e => e.id === expositor.id)
        );
    } else {
      expositorVinculados = vinculosMontadoras
        .filter(vinculo => vinculo.montadora_id === company.id)
        .map(vinculo => ({
          id: vinculo.expositor_id,
          nome_empresa: vinculo.caex_expositores?.nome_empresa || 'N/A'
        }))
        .filter((expositor, index, self) => 
          index === self.findIndex(e => e.id === expositor.id)
        );
    }

    setFormData({
      nome: company.nome || '',
      cnpj: company.cnpj || '',
      responsavel: company.responsavel || '',
      cpf_responsavel: company.cpf_responsavel ? formatCpf(company.cpf_responsavel) : '',
      email: company.email || '',
      telefone: company.telefone ? formatTelefone(company.telefone) : '',
      selectedExpositores: expositorVinculados,
      ativo: company.ativo !== undefined ? company.ativo : true
    });
    setShowForm(true);
    setActiveModalTab('empresa');
  }, [isExpositor, expositorLogado, vinculosMontadoras]);

  const handleDeleteCompany = useCallback((company) => {
    setEditingCompany(company);
    setShowDelete(true);
  }, []);

  const handleUnlinkCompany = useCallback((company) => {
    setUnlinkingCompany(company);
    setShowUnlink(true);
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const handleCnpjChange = useCallback((e) => {
    const formattedCnpj = formatCnpj(e.target.value);
    setFormData(prev => ({
      ...prev,
      cnpj: formattedCnpj
    }));
    
    if (cnpjAutoFilled) {
      setCnpjAutoFilled(false);
      setEditingCompany(null);
    }
  }, [cnpjAutoFilled]);

  const handleCpfChange = useCallback((e) => {
    const formattedCpf = formatCpf(e.target.value);
    setFormData(prev => ({
      ...prev,
      cpf_responsavel: formattedCpf
    }));
  }, []);

  const handleTelefoneChange = useCallback((e) => {
    const formattedTelefone = formatTelefone(e.target.value);
    setFormData(prev => ({
      ...prev,
      telefone: formattedTelefone
    }));
  }, []);

  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  // Função de submissão simplificada
  const handleSubmit = useCallback(async (e) => {
    if (e) e.preventDefault();
    
    if (!formData.nome || !formData.cnpj || !formData.responsavel || !formData.cpf_responsavel || !formData.telefone) {
      alert('Por favor, preencha todos os campos obrigatórios: Nome da Empresa, CNPJ, Nome do Responsável, CPF e Telefone.');
      return;
    }
    
    try {
      const empresaData = {
        nome: formData.nome,
        cnpj: removeCnpjMask(formData.cnpj),
        responsavel: formData.responsavel,
        cpf_responsavel: removeCpfMask(formData.cpf_responsavel),
        email: formData.email || null,
        telefone: removeTelefoneMask(formData.telefone),
        ativo: formData.ativo,
        updated_at: new Date()
      };

      let montadoraId;

      if (isExpositor && editingCompany && expositorLogado) {
        montadoraId = editingCompany.id;
        
        const { data: expositorEventos, error: eventosError } = await supabase
          .from('caex_expositor_evento')
          .select('evento_id')
          .eq('expositor_id', expositorLogado.id);
        
        if (eventosError) {
          console.error('Erro ao buscar eventos do expositor:', eventosError);
          throw new Error('Erro ao buscar eventos do expositor');
        }

        if (expositorEventos && expositorEventos.length > 0) {
          const novosVinculos = expositorEventos.map(rel => ({
            montadora_id: montadoraId,
            expositor_id: expositorLogado.id,
            evento_id: rel.evento_id,
            ativo: true,
            created_at: new Date()
          }));
          
          for (const vinculo of novosVinculos) {
            const { data: vinculoExistente } = await supabase
              .from('caex_montadora_expositor_evento')
              .select('id')
              .eq('montadora_id', vinculo.montadora_id)
              .eq('expositor_id', vinculo.expositor_id)
              .eq('evento_id', vinculo.evento_id)
              .single();
            
            if (!vinculoExistente) {
              const { error: vinculoError } = await supabase
                .from('caex_montadora_expositor_evento')
                .insert([vinculo]);
              
              if (vinculoError) {
                console.error('Erro ao criar vínculo:', vinculoError);
              }
            }
          }
        } else if (eventos.length > 0) {
          const { data: vinculoExistente } = await supabase
            .from('caex_montadora_expositor_evento')
            .select('id')
            .eq('montadora_id', montadoraId)
            .eq('expositor_id', expositorLogado.id)
            .eq('evento_id', eventos[0].id)
            .single();
          
          if (!vinculoExistente) {
            const { error: vinculoError } = await supabase
              .from('caex_montadora_expositor_evento')
              .insert([{
                montadora_id: montadoraId,
                expositor_id: expositorLogado.id,
                evento_id: eventos[0].id,
                ativo: true,
                created_at: new Date()
              }]);
            
            if (vinculoError) {
              console.error('Erro ao criar vínculo:', vinculoError);
            }
          }
        }
        
        await fetchVinculosMontadoras(expositorLogado);
        await fetchEmpresasMontadoras();
        
        resetForm();
        alert('Empresa montadora vinculada com sucesso!');
      } else {
        if (editingCompany && !isExpositor) {
          const { error } = await supabase
            .from('caex_empresas_montadoras')
            .update(empresaData)
            .eq('id', editingCompany.id);
          
          if (error) throw error;
          
          montadoraId = editingCompany.id;
          
          setEmpresasMontadoras(prev => prev.map(empresa => 
            empresa.id === editingCompany.id ? { 
              ...empresa, 
              ...empresaData,
              cnpj: formatCnpj(empresaData.cnpj)
            } : empresa
          ));
        } else {
          const { data, error } = await supabase
            .from('caex_empresas_montadoras')
            .insert([empresaData])
            .select('*');
          
          if (error) throw error;
          
          montadoraId = data[0].id;
          
          const newEmpresa = {
            ...data[0],
            cnpj: formatCnpj(data[0].cnpj)
          };
          
          setEmpresasMontadoras(prev => [newEmpresa, ...prev]);
        }
      }
      
      // Lógica de vínculos para admins
      if (montadoraId && !isExpositor) {
        await supabase
          .from('caex_montadora_expositor_evento')
          .delete()
          .eq('montadora_id', montadoraId);
        
        if (formData.selectedExpositores && formData.selectedExpositores.length > 0) {
          for (const expositor of formData.selectedExpositores) {
            const { data: expositorEventos, error: eventosError } = await supabase
              .from('caex_expositor_evento')
              .select('evento_id')
              .eq('expositor_id', expositor.id);
            
            if (eventosError) {
              console.error('Erro ao buscar eventos do expositor:', eventosError);
              continue;
            }
            
            if (expositorEventos && expositorEventos.length > 0) {
              const novosVinculos = expositorEventos.map(rel => ({
                montadora_id: montadoraId,
                expositor_id: expositor.id,
                evento_id: rel.evento_id,
                ativo: true,
                created_at: new Date()
              }));
              
              const { error: vinculosError } = await supabase
                .from('caex_montadora_expositor_evento')
                .insert(novosVinculos);
              
              if (vinculosError) {
                console.error('Erro ao salvar vínculos:', vinculosError);
              }
            } else if (eventos.length > 0) {
              const { error: vinculoError } = await supabase
                .from('caex_montadora_expositor_evento')
                .insert([{
                  montadora_id: montadoraId,
                  expositor_id: expositor.id,
                  evento_id: eventos[0].id,
                  ativo: true,
                  created_at: new Date()
                }]);
              
              if (vinculoError) {
                console.error('Erro ao salvar vínculo:', vinculoError);
              }
            }
          }
        }
        
        await fetchVinculosMontadoras(expositorLogado);
      }
      
      if (!(isExpositor && editingCompany)) {
        resetForm();
      }
      
    } catch (error) {
      console.error('Erro ao salvar empresa montadora:', error);
      alert(`Não foi possível salvar a empresa montadora. Erro: ${error.message || 'Desconhecido'}`);
    }
  }, [formData, editingCompany, isExpositor, expositorLogado, eventos, fetchVinculosMontadoras, fetchEmpresasMontadoras, resetForm]);

  // Função para desvincular empresa
  const unlinkEmpresa = useCallback(async (montadoraId) => {
    if (!expositorLogado?.id) return;
    
    try {
      const { error } = await supabase
        .from('caex_montadora_expositor_evento')
        .delete()
        .eq('montadora_id', montadoraId)
        .eq('expositor_id', expositorLogado.id);
      
      if (error) throw error;
      
      setShowUnlink(false);
      setUnlinkingCompany(null);
      
      await fetchVinculosMontadoras(expositorLogado);
      await fetchEmpresasMontadoras();
      
      alert('Empresa montadora desvinculada com sucesso!');
    } catch (error) {
      console.error('Erro ao desvincular empresa montadora:', error);
      alert('Não foi possível desvincular a empresa montadora. Por favor, tente novamente.');
    }
  }, [expositorLogado, fetchVinculosMontadoras, fetchEmpresasMontadoras]);

  // Função para deletar empresa
  const deleteEmpresa = useCallback(async (id) => {
    try {
      const { error: vinculosError } = await supabase
        .from('caex_montadora_expositor_evento')
        .delete()
        .eq('montadora_id', id);
      
      if (vinculosError) {
        console.error('Erro ao excluir vínculos:', vinculosError);
      }
      
      const { error } = await supabase
        .from('caex_empresas_montadoras')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      setEmpresasMontadoras(prev => prev.filter(empresa => empresa.id !== id));
      setShowDelete(false);
      
      await fetchVinculosMontadoras(expositorLogado);
    } catch (error) {
      console.error('Erro ao excluir empresa montadora:', error);
      alert('Não foi possível excluir a empresa montadora. Por favor, tente novamente.');
    }
  }, [expositorLogado, fetchVinculosMontadoras]);

  // Memorizar empresas filtradas
  const filteredMontadoras = useMemo(() => {
    if (!Array.isArray(empresasMontadoras)) return [];
    
    return empresasMontadoras.filter(montadora => {
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        return montadora?.nome?.toLowerCase().includes(searchTerm) || 
               montadora?.cnpj?.includes(searchTerm) ||
               montadora?.responsavel?.toLowerCase().includes(searchTerm) ||
               montadora?.cpf_responsavel?.includes(searchTerm);
      }
      return true;
    });
  }, [empresasMontadoras, filters.search]);

  // Renderizar expositores vinculados
  const renderExpositorVinculados = useCallback((montadora) => {
    if (isExpositor && expositorLogado) {
      return (
        <span style={{
          fontSize: '0.7rem',
          padding: '0.2rem 0.5rem',
          backgroundColor: '#d4edda',
          color: '#155724',
          borderRadius: '15px',
          border: '1px solid #c3e6cb',
          fontWeight: '600',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.25rem'
        }}>
          <FaLink size={10} />
          Vinculado
        </span>
      );
    } else {
      const expositorVinculados = vinculosMontadoras
        .filter(vinculo => vinculo.montadora_id === montadora.id)
        .map(vinculo => vinculo.caex_expositores?.nome_empresa)
        .filter((nome, index, self) => nome && self.indexOf(nome) === index);
      
      if (expositorVinculados.length > 0) {
        return (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.25rem'
          }}>
            {expositorVinculados.map((nomeExpositor, index) => (
              <span key={index} style={{
                fontSize: '0.7rem',
                padding: '0.2rem 0.5rem',
                backgroundColor: '#e3f2fd',
                color: '#1976d2',
                borderRadius: '15px',
                border: '1px solid #bbdefb'
              }}>
                {nomeExpositor}
              </span>
            ))}
          </div>
        );
      } else {
        return (
          <span style={{ color: '#6c757d', fontSize: '0.8rem' }}>
            Nenhum expositor
          </span>
        );
      }
    }
  }, [isExpositor, expositorLogado, vinculosMontadoras]);

  // Estilos modais
  const modalStyles = useMemo(() => ({
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem',
      backdropFilter: 'blur(5px)'
    },
    content: {
      position: 'relative',
      width: '100%',
      maxWidth: '700px',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'white',
      borderRadius: '20px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      overflow: 'hidden'
    },
    header: {
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
      padding: '1.5rem 2rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexShrink: 0
    },
    closeButton: {
      width: '36px',
      height: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      border: 'none',
      background: 'white',
      color: '#6c757d',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
    },
    title: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: '#1a1a1a',
      margin: '0',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem'
    },
    tabs: {
      display: 'flex',
      borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
      backgroundColor: 'white',
      flexShrink: 0
    },
    body: {
      padding: '2rem',
      overflowY: 'auto',
      flex: '1 1 auto'
    },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      padding: '1.5rem 2rem',
      borderTop: '1px solid rgba(0, 0, 0, 0.05)',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      flexShrink: 0
    },
    footerButton: {
      padding: '0.75rem 1.5rem',
      borderRadius: '50px',
      fontSize: '0.9rem',
      fontWeight: '600',
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    },
    cancelButton: {
      border: '1px solid #dee2e6',
      background: 'white',
      color: '#495057',
      marginRight: '0.75rem'
    },
    submitButton: {
      border: 'none',
      background: '#212529',
      color: 'white',
      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)'
    },
    requiredField: {
      color: '#dc3545',
      marginLeft: '0.25rem'
    },
    requiredNotice: {
      fontSize: '0.8rem',
      color: '#dc3545',
      marginBottom: '1rem'
    }
  }), []);

  // Conteúdo das abas do modal
  const renderTabContent = useCallback(() => {
    switch (activeModalTab) {
      case 'empresa':
        return (
          <>
            {isExpositor && !editingCompany && (
              <div style={{
                marginBottom: '1.5rem',
                padding: '1rem',
                backgroundColor: '#e3f2fd',
                color: '#1976d2',
                borderRadius: '8px',
                border: '1px solid #bbdefb',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem'
              }}>
                <FaInfoCircle style={{marginTop: '0.2rem'}} />
                <div>
                  <strong>Vinculação de Empresa Montadora</strong><br />
                  <small>
                    Digite o CNPJ de uma empresa montadora existente para vinculá-la ao seu perfil. 
                    A vinculação será feita automaticamente para todos os eventos em que você está participando.
                  </small>
                </div>
              </div>
            )}
            
            {!isExpositor && cnpjAutoFilled && (
              <div style={{
                marginBottom: '1.5rem',
                padding: '1rem',
                backgroundColor: '#fff3cd',
                color: '#856404',
                borderRadius: '8px',
                border: '1px solid #ffeeba',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem'
              }}>
                <FaInfoCircle style={{marginTop: '0.2rem'}} />
                <div>
                  <strong>Empresa Existente Encontrada</strong><br />
                  <small>
                    Os expositores já vinculados a esta empresa foram carregados automaticamente. 
                    Você pode adicionar novos expositores ou remover os existentes conforme necessário.
                  </small>
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label" htmlFor="cnpj">
                CNPJ <span style={modalStyles.requiredField}>*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  id="cnpj"
                  name="cnpj"
                  value={formData.cnpj || ''}
                  onChange={handleCnpjChange}
                  className="form-input"
                  placeholder="00.000.000/0000-00"
                  maxLength="18"
                  required
                  readOnly={isExpositor && editingCompany}
                  style={{
                    paddingRight: checkingCNPJ ? '45px' : '15px',
                    borderColor: cnpjAutoFilled ? '#28a745' : '#dee2e6',
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: '1px solid #dee2e6',
                    borderRadius: '10px',
                    fontSize: '0.9rem',
                    color: '#495057',
                    transition: 'all 0.3s ease'
                  }}
                />
                {checkingCNPJ && (
                  <div style={{
                    position: 'absolute',
                    right: '15px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#6c757d'
                  }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #dee2e6',
                      borderTop: '2px solid #007bff',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                  </div>
                )}
              </div>
              {cnpjAutoFilled && (
                <div style={{
                  marginTop: '0.5rem',
                  padding: '0.5rem',
                  backgroundColor: '#d4edda',
                  color: '#155724',
                  borderRadius: '5px',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <FaCheck size={14} />
                  {isExpositor 
                    ? 'Dados preenchidos automaticamente para CNPJ existente'
                    : 'Empresa encontrada! Dados e expositores vinculados carregados automaticamente.'
                  }
                </div>
              )}
            </div>
            
            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label className="form-label" htmlFor="nome" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.9rem',
                fontWeight: '600',
                color: '#495057'
              }}>
                Nome da Empresa <span style={modalStyles.requiredField}>*</span>
              </label>
              <input
                type="text"
                id="nome"
                name="nome"
                value={formData.nome || ''}
                onChange={handleChange}
                placeholder="Digite o nome da empresa montadora"
                required
                readOnly={isExpositor && editingCompany}
                style={{
                  backgroundColor: cnpjAutoFilled ? '#f8f9fa' : 'white',
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '10px',
                  fontSize: '0.9rem',
                  color: '#495057',
                  transition: 'all 0.3s ease'
                }}
              />
            </div>
            
            {(!isExpositor || (isExpositor && !editingCompany)) && (
              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: '#495057'
                }}>
                  <input
                    type="checkbox"
                    name="ativo"
                    checked={Boolean(formData.ativo)}
                    onChange={handleChange}
                    style={{ marginRight: '0.5rem' }}
                    disabled={isExpositor && editingCompany}
                  />
                  Empresa Ativa
                </label>
                <small className="text-muted">
                  Marque para manter a empresa ativa no sistema
                </small>
              </div>
            )}
            
            <div style={modalStyles.requiredNotice}>
              * Campos obrigatórios
            </div>
          </>
        );
        
      case 'responsavel':
        return (
          <>
            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label className="form-label" htmlFor="responsavel" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.9rem',
                fontWeight: '600',
                color: '#495057'
              }}>
                Nome Completo do Responsável <span style={modalStyles.requiredField}>*</span>
              </label>
              <input
                type="text"
                id="responsavel"
                name="responsavel"
                value={formData.responsavel || ''}
                onChange={handleChange}
                placeholder="Digite o nome completo do responsável"
                required
                readOnly={isExpositor && editingCompany}
                style={{
                  backgroundColor: cnpjAutoFilled ? '#f8f9fa' : 'white',
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '10px',
                  fontSize: '0.9rem',
                  color: '#495057',
                  transition: 'all 0.3s ease'
                }}
              />
            </div>
            
            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label className="form-label" htmlFor="cpf_responsavel" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.9rem',
                fontWeight: '600',
                color: '#495057'
              }}>
                CPF <span style={modalStyles.requiredField}>*</span>
              </label>
              <input
                type="text"
                id="cpf_responsavel"
                name="cpf_responsavel"
                value={formData.cpf_responsavel || ''}
                onChange={handleCpfChange}
                placeholder="000.000.000-00"
                maxLength="14"
                required
                readOnly={isExpositor && editingCompany}
                style={{
                  backgroundColor: cnpjAutoFilled ? '#f8f9fa' : 'white',
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '10px',
                  fontSize: '0.9rem',
                  color: '#495057',
                  transition: 'all 0.3s ease'
                }}
              />
            </div>
            
            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label className="form-label" htmlFor="telefone" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.9rem',
                fontWeight: '600',
                color: '#495057'
              }}>
                Telefone <span style={modalStyles.requiredField}>*</span>
              </label>
              <input
                type="text"
                id="telefone"
                name="telefone"
                value={formData.telefone || ''}
                onChange={handleTelefoneChange}
                placeholder="(00) 00000-0000"
                maxLength="15"
                required
                readOnly={isExpositor && editingCompany}
                style={{
                  backgroundColor: cnpjAutoFilled ? '#f8f9fa' : 'white',
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '10px',
                  fontSize: '0.9rem',
                  color: '#495057',
                  transition: 'all 0.3s ease'
                }}
              />
            </div>
            
            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label className="form-label" htmlFor="email" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.9rem',
                fontWeight: '600',
                color: '#495057'
              }}>
                E-mail
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email || ''}
                onChange={handleChange}
                placeholder="email@exemplo.com"
                readOnly={isExpositor && editingCompany}
                style={{
                  backgroundColor: cnpjAutoFilled ? '#f8f9fa' : 'white',
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '10px',
                  fontSize: '0.9rem',
                  color: '#495057',
                  transition: 'all 0.3s ease'
                }}
              />
            </div>

            {!isExpositor && (
              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label className="form-label" style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: '#495057'
                }}>
                  Expositores Vinculados
                </label>
                <div style={{ position: 'relative' }} ref={expositorDropdownRef}>
                  <div
                    style={{
                      border: '2px solid #e9ecef',
                      borderRadius: '8px',
                      padding: '0.75rem',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      transition: 'border-color 0.3s ease'
                    }}
                    onClick={() => setShowExpositorDropdown(!showExpositorDropdown)}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem'
                    }}>
                      <FaSearch style={{ color: '#6c757d', fontSize: '0.9rem' }} />
                      <input
                        type="text"
                        value={expositorSearch}
                        onChange={handleExpositorSearch}
                        placeholder="Buscar expositores..."
                        style={{
                          border: 'none',
                          outline: 'none',
                          flex: 1,
                          backgroundColor: 'transparent',
                          fontSize: '0.95rem',
                          color: '#495057'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowExpositorDropdown(true);
                        }}
                      />
                      <span style={{
                        fontSize: '0.75rem',
                        color: '#6c757d',
                        fontWeight: '500'
                      }}>
                        {showExpositorDropdown ? '▲' : '▼'}
                      </span>
                    </div>
                  </div>

                  {showExpositorDropdown && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        marginTop: '4px',
                        backgroundColor: 'white',
                        border: '1px solid #e9ecef',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        maxHeight: '250px',
                        overflow: 'hidden'
                      }}
                    >
                      <div style={{
                        padding: '0.75rem 1rem',
                        borderBottom: '1px solid #f1f3f4',
                        backgroundColor: '#f8f9fa',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        color: '#495057'
                      }}>
                        {loadingExpositores ? (
                          <span>🔄 Carregando expositores...</span>
                        ) : (
                          <span>🏢 {filteredExpositores.length} expositor(es) encontrado(s)</span>
                        )}
                      </div>

                      <div style={{
                        maxHeight: '200px',
                        overflowY: 'auto'
                      }}>
                        {!loadingExpositores && filteredExpositores.length > 0 ? (
                          filteredExpositores.map(expositor => {
                            const isSelected = (formData.selectedExpositores || []).some(e => e.id === expositor.id);
                            return (
                              <div
                                key={expositor.id}
                                onClick={() => {
                                  handleAddExpositor(expositor);
                                  setShowExpositorDropdown(false);
                                }}
                                style={{
                                  padding: '0.75rem 1rem',
                                  borderBottom: '1px solid #f8f9fa',
                                  cursor: 'pointer',
                                  backgroundColor: isSelected ? '#e3f2fd' : 'white',
                                  transition: 'background-color 0.2s ease',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}
                                onMouseEnter={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = isSelected ? '#e3f2fd' : 'white';
                                }}
                              >
                                <div>
                                  <div style={{
                                    fontWeight: '500',
                                    fontSize: '0.9rem',
                                    color: '#2c3e50',
                                    marginBottom: '0.25rem'
                                  }}>
                                    {expositor.nome_empresa}
                                  </div>
                                  <div style={{
                                    fontSize: '0.8rem',
                                    color: '#6c757d'
                                  }}>
                                    CNPJ: {expositor.cnpj || 'N/A'}
                                  </div>
                                </div>
                                {isSelected && (
                                  <span style={{
                                    color: '#1976d2',
                                    fontWeight: 'bold',
                                    fontSize: '0.9rem'
                                  }}>
                                    ✓
                                  </span>
                                )}
                              </div>
                            );
                          })
                        ) : !loadingExpositores && (
                          <div style={{
                            padding: '2rem 1rem',
                            textAlign: 'center',
                            color: '#6c757d'
                          }}>
                            <FaSearch style={{ fontSize: '2rem', opacity: 0.3, marginBottom: '0.5rem' }} />
                            <div style={{ fontSize: '0.9rem' }}>
                              {expositorSearch ? `Nenhum expositor encontrado para "${expositorSearch}"` : 'Nenhum expositor disponível'}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {formData.selectedExpositores && formData.selectedExpositores.length > 0 && (
                  <div style={{
                    marginTop: '0.75rem',
                    padding: '0.75rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #e9ecef'
                  }}>
                    <div style={{
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      color: '#495057',
                      marginBottom: '0.5rem'
                    }}>
                      Expositores Selecionados ({formData.selectedExpositores.length}):
                    </div>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '0.5rem'
                    }}>
                      {formData.selectedExpositores.map(expositor => (
                        <div
                          key={expositor.id}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.4rem 0.75rem',
                            backgroundColor: '#e3f2fd',
                            color: '#1976d2',
                            borderRadius: '20px',
                            fontSize: '0.8rem',
                            fontWeight: '500',
                            border: '1px solid #bbdefb'
                          }}
                        >
                          <FaBuilding size={12} />
                          <span>{expositor.nome_empresa}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveExpositor(expositor.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#1976d2',
                              cursor: 'pointer',
                              padding: '2px',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '18px',
                              height: '18px',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#f44336';
                              e.target.style.color = 'white';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                              e.target.style.color = '#1976d2';
                            }}
                            title="Remover expositor"
                          >
                            <FaTimes size={10} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <small className="text-muted">
                  Digite para buscar expositores e clique para selecionar
                </small>
              </div>
            )}
            
            <div style={modalStyles.requiredNotice}>
              * Campos obrigatórios
            </div>
          </>
        );
        
      default:
        return null;
    }
  }, [activeModalTab, modalStyles, formData, handleChange, handleCnpjChange, handleCpfChange, handleTelefoneChange, isExpositor, editingCompany, showExpositorDropdown, expositorSearch, handleExpositorSearch, loadingExpositores, filteredExpositores, handleAddExpositor, handleRemoveExpositor, checkingCNPJ, cnpjAutoFilled]);

  // CSS inline para animações
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      
      .modal-tab {
        position: relative;
        padding: 1rem 1.5rem;
        font-weight: 500;
        color: #6c757d;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }
      
      .modal-tab-active {
        color: #0d6efd;
        font-weight: 600;
      }
      
      .modal-tab-active::after {
        content: '';
        position: absolute;
        bottom: -1px;
        left: 0;
        right: 0;
        height: 2px;
        background-color: #0d6efd;
      }
      
      .modal-tab:hover:not(.modal-tab-active) {
        color: #495057;
        background-color: rgba(0, 0, 0, 0.02);
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  if (loading || checkingProfile) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-hub">
          <div className="dashboard-header">
            <div className="dashboard-title-section">
              <h1 className="dashboard-main-title">
                <FaBuilding className="dashboard-title-icon" />
                Empresas Montadoras
              </h1>
            </div>
          </div>
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-900"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-hub">
          <div className="dashboard-header">
            <div className="dashboard-title-section">
              <h1 className="dashboard-main-title">
                <FaBuilding className="dashboard-title-icon" />
                Empresas Montadoras
              </h1>
            </div>
          </div>
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative my-4" role="alert">
            <strong className="font-bold">Erro: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="dashboard-container">
        <div className="dashboard-hub">
          <div className="dashboard-header">
            <div className="dashboard-title-section">
              <h1 className="dashboard-main-title">
                <FaBuilding className="dashboard-title-icon" />
                {isExpositor ? 'Minhas Empresas Montadoras' : 'Empresas Montadoras'}
              </h1>
              <p className="dashboard-subtitle">
                {isExpositor 
                  ? 'Gerencie suas empresas montadoras vinculadas ou vincule novas através do botão "Nova Empresa Montadora"'
                  : 'Gerenciamento de empresas montadoras e seus vínculos com expositores'
                }
              </p>
            </div>
          </div>

          <div className="d-flex justify-content-end mb-4">
            <Button 
              variant="outline-secondary" 
              className="me-2"
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? <FaTimes className="me-2" /> : <FaFilter className="me-2" />}
              {showFilters ? 'Ocultar Filtros' : 'Filtros'}
            </Button>
            
            <Button 
              variant="primary" 
              onClick={handleAddCompany}
              className="bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800 transition-all flex items-center gap-2"
              style={{ boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}
            >
              <FaPlus />
              Nova Empresa Montadora
            </Button>
          </div>

          {showFilters && (
            <Card className="modern-table-card mb-4">
              <Card.Body className="modern-card-body">
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Buscar</Form.Label>
                      <div className="position-relative">
                        <Form.Control 
                          type="text" 
                          placeholder="Digite para buscar..."
                          name="search"
                          value={filters.search}
                          onChange={handleFilterChange}
                        />
                        <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted" />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          <div className="dashboard-section-header">
            <h2 className="dashboard-section-title">
              <FaBuilding className="dashboard-section-icon" />
              {isExpositor ? 'Minhas Empresas Montadoras' : 'Empresas Montadoras'}
            </h2>
            <p className="dashboard-section-subtitle">
              {isExpositor 
                ? 'Empresas montadoras vinculadas ao seu perfil'
                : 'Visualize e gerencie as empresas montadoras cadastradas no sistema'
              }
            </p>
          </div>

          <Card className="modern-table-card">
            <Card.Body className="modern-card-body">
              <div className="modern-table-container">
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th>Empresa</th>
                      <th>CNPJ</th>
                      <th>Responsável</th>
                      <th>CPF</th>
                      <th>Contato</th>
                      <th>{isExpositor ? 'Status do Vínculo' : 'Expositores Vinculados'}</th>
                      <th>Status</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMontadoras.length > 0 ? (
                      filteredMontadoras.map(montadora => (
                        <tr key={montadora.id}>
                          <td className="font-weight-bold">
                            {montadora.nome}
                          </td>
                          <td>{montadora.cnpj}</td>
                          <td>{montadora.responsavel}</td>
                          <td>{montadora.cpf_responsavel ? formatCpf(montadora.cpf_responsavel) : '-'}</td>
                          <td>
                            {montadora.telefone ? formatTelefone(montadora.telefone) : '-'}<br />
                            <small>{montadora.email || '-'}</small>
                          </td>
                          <td>
                            {renderExpositorVinculados(montadora)}
                          </td>
                          <td>
                            <span className="modern-badge" style={{ 
                              backgroundColor: montadora.ativo ? '#28a745' : '#dc3545',
                              color: 'white',
                              padding: '0.4rem 0.8rem',
                              borderRadius: '20px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              {montadora.ativo ? 'Ativo' : 'Inativo'}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              {isExpositor ? (
                                <>
                                  <Button 
                                    variant="outline-primary" 
                                    size="sm"
                                    onClick={() => handleEditCompany(montadora)}
                                    className="me-1"
                                    title="Visualizar"
                                  >
                                    <FaEdit />
                                  </Button>
                                  <Button 
                                    variant="outline-warning" 
                                    size="sm"
                                    onClick={() => handleUnlinkCompany(montadora)}
                                    title="Desvincular"
                                  >
                                    <FaUnlink />
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button 
                                    variant="outline-primary" 
                                    size="sm"
                                    onClick={() => handleEditCompany(montadora)}
                                    className="me-1"
                                    title="Editar"
                                  >
                                    <FaEdit />
                                  </Button>
                                  <Button 
                                    variant="outline-danger" 
                                    size="sm"
                                    onClick={() => handleDeleteCompany(montadora)}
                                    title="Excluir"
                                  >
                                    <FaTrash />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center py-4">
                          <p className="text-muted mb-0">
                            {isExpositor 
                              ? 'Nenhuma empresa montadora vinculada. Use o botão "Nova Empresa Montadora" para vincular uma empresa pelo CNPJ.' 
                              : 'Nenhuma empresa montadora encontrada'
                            }
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Modal de formulário */}
      {showForm && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.content}>
            <div style={modalStyles.header}>
              <h2 style={modalStyles.title}>
                {editingCompany 
                  ? <FaEdit style={{color: '#6c757d'}} /> 
                  : <FaPlus style={{color: '#6c757d'}} />
                }
                {(() => {
                  if (isExpositor && editingCompany) {
                    return 'Visualizar Empresa Montadora';
                  } else if (isExpositor && !editingCompany) {
                    return 'Vincular Nova Empresa Montadora';
                  }
                  return editingCompany ? 'Editar Empresa Montadora' : 'Nova Empresa Montadora';
                })()}
              </h2>
              <button 
                onClick={resetForm}
                style={modalStyles.closeButton}
                className="modal-close-btn"
                aria-label="Fechar"
              >
                ✕
              </button>
            </div>

            <div style={modalStyles.tabs}>
              <div 
                className={`modal-tab ${activeModalTab === 'empresa' ? 'modal-tab-active' : ''}`}
                onClick={() => setActiveModalTab('empresa')}
              >
                <FaBuilding />
                Dados da Empresa
              </div>
              <div 
                className={`modal-tab ${activeModalTab === 'responsavel' ? 'modal-tab-active' : ''}`}
                onClick={() => setActiveModalTab('responsavel')}
              >
                <FaAddressCard />
                Responsável
              </div>
            </div>
            
            <div style={modalStyles.body} className="modal-scrollable-body">
              {renderTabContent()}
            </div>
            
            <div style={modalStyles.footer}>
              <button
                type="button"
                onClick={resetForm}
                style={{...modalStyles.footerButton, ...modalStyles.cancelButton}}
                className="btn-cancel"
              >
                {isExpositor && editingCompany && !cnpjAutoFilled ? 'Fechar' : 'Cancelar'}
              </button>
              {(() => {
                if (isExpositor && editingCompany && !cnpjAutoFilled) {
                  return null;
                }
                
                return (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    style={{...modalStyles.footerButton, ...modalStyles.submitButton}}
                    className="btn-submit"
                  >
                    {isExpositor ? 'Vincular Empresa' : (editingCompany ? 'Atualizar' : 'Salvar')}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Modal de exclusão */}
      {showDelete && !isExpositor && (
        <div style={modalStyles.overlay}>
          <div style={{...modalStyles.content, maxWidth: '500px'}}>
            <div style={modalStyles.header}>
              <h2 style={modalStyles.title}>
                <FaTrash style={{color: '#dc3545'}} />
                Confirmar Exclusão
              </h2>
              <button 
                onClick={() => setShowDelete(false)}
                style={modalStyles.closeButton}
                className="modal-close-btn"
                aria-label="Fechar"
              >
                ✕
              </button>
            </div>
            
            <div style={modalStyles.body} className="modal-scrollable-body">
              {editingCompany && (
                <p style={{fontSize: '1rem', marginBottom: '1.5rem'}}>
                  Tem certeza que deseja excluir a empresa <strong>{editingCompany.nome}</strong>?
                </p>
              )}
              
              <div style={{
                backgroundColor: '#fff3cd',
                color: '#856404',
                padding: '1rem',
                borderRadius: '10px',
                border: '1px solid #ffeeba',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem'
              }}>
                <FaExclamationTriangle style={{marginTop: '0.2rem'}} />
                <div>
                  Esta ação não pode ser desfeita e todos os vínculos desta montadora serão removidos.
                </div>
              </div>
            </div>
            
            <div style={modalStyles.footer}>
              <button
                type="button"
                onClick={() => setShowDelete(false)}
                style={{...modalStyles.footerButton, ...modalStyles.cancelButton}}
                className="btn-cancel"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => deleteEmpresa(editingCompany?.id)}
                style={{
                  ...modalStyles.footerButton,
                  ...modalStyles.submitButton,
                  background: '#dc3545'
                }}
                className="btn-submit"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de desvinculação */}
      {showUnlink && isExpositor && (
        <div style={modalStyles.overlay}>
          <div style={{...modalStyles.content, maxWidth: '500px'}}>
            <div style={modalStyles.header}>
              <h2 style={modalStyles.title}>
                <FaUnlink style={{color: '#ff9800'}} />
                Confirmar Desvinculação
              </h2>
              <button 
                onClick={() => setShowUnlink(false)}
                style={modalStyles.closeButton}
                className="modal-close-btn"
                aria-label="Fechar"
              >
                ✕
              </button>
            </div>
            
            <div style={modalStyles.body} className="modal-scrollable-body">
              {unlinkingCompany && (
                <p style={{fontSize: '1rem', marginBottom: '1.5rem'}}>
                  Tem certeza que deseja se desvincular da empresa <strong>{unlinkingCompany.nome}</strong>?
                </p>
              )}
              
              <div style={{
                backgroundColor: '#fff3cd',
                color: '#856404',
                padding: '1rem',
                borderRadius: '10px',
                border: '1px solid #ffeeba',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem'
              }}>
                <FaExclamationTriangle style={{marginTop: '0.2rem'}} />
                <div>
                  Ao desvincular, esta empresa montadora não aparecerá mais na sua lista de empresas vinculadas 
                  e você perderá o acesso aos serviços desta montadora para todos os eventos.
                </div>
              </div>
            </div>
            
            <div style={modalStyles.footer}>
              <button
                type="button"
                onClick={() => setShowUnlink(false)}
                style={{...modalStyles.footerButton, ...modalStyles.cancelButton}}
                className="btn-cancel"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => unlinkEmpresa(unlinkingCompany?.id)}
                style={{
                  ...modalStyles.footerButton,
                  ...modalStyles.submitButton,
                  background: '#ff9800'
                }}
                className="btn-submit"
              >
                Desvincular
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EmpresasMontadoras;